import { action, computed, makeObservable, observable } from 'mobx';
import { defaultOrderSettings } from '../constants/orderConfiguration';
import { IOrderSettingsErrorMessages, orderSettingsCast, TOrderSettings } from '../types/orderConfiguration';
import client from 'src/services/client';
import { LoadingStatus } from 'src/types/main';
import { numberValidation } from 'src/utils/validation';
import logger from '~/services/logger';

class OrderConfigurationStore {
  constructor() {
    makeObservable(this);
  }

  @observable orderSettings: TOrderSettings = defaultOrderSettings;
  @observable settingsLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable errorMessages: IOrderSettingsErrorMessages = { vat: null, certificate: null, vatRegNumber: null };
  @observable isDataChanged = false;

  @action changeOrderConditions = (data: Partial<TOrderSettings['config']>) => {
    this.orderSettings.config = { ...this.orderSettings.config, ...data };
    this.isDataChanged = true;
  };

  @action changeOrderNotes = (data: Partial<TOrderSettings['notes']>) => {
    this.orderSettings.notes = { ...this.orderSettings.notes, ...data };
    this.isDataChanged = true;
  };

  @action fetchOrderSettings = async () => {
    try {
      this.settingsLoadingStatus = LoadingStatus.Loading;

      const settings = await client.get('v3/settings/order/config');
      this.orderSettings = { ...this.orderSettings, ...orderSettingsCast(settings) };

      this.settingsLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.settingsLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'OrderConfigurationStore fetchOrderSettings' });
    }
  };

  @action updateOrderSettings = async () => {
    try {
      this.settingsLoadingStatus = LoadingStatus.Refreshing;
      await client.post('v3/settings/order/config', this._formedApiData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      this.settingsLoadingStatus = LoadingStatus.Success;
      this.isDataChanged = false;
    } catch (error: any) {
      this.settingsLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'OrderConfigurationStore updateOrderSettings' });
    }
  };

  @action validateVat = () => {
    this.errorMessages.vat = !this.orderSettings.config.vat ? 'required_field_error' : null;
  };

  @action validateVatRegNumber = () => {
    const isValidRegNumber = numberValidation(this.orderSettings.config.vatRegistrationNumber);
    this.errorMessages.vatRegNumber = !isValidRegNumber ? 'Only_digits' : null;
  };

  @action validateCertificateFile = () => {
    const isValidCertificateFile = this.orderSettings.config.certificate.type === 'application/pdf';

    if (!isValidCertificateFile) {
      this.errorMessages.certificate = 'Please upload PDF file';
      this.orderSettings.config.certificate = new File([], '');
      this.orderSettings.config.certificateFilename = '';
    } else {
      this.errorMessages.certificate = null;
    }
  };

  @action reset = () => {
    this.orderSettings = defaultOrderSettings;
    this.errorMessages = { vat: null, certificate: null, vatRegNumber: null };
    this.settingsLoadingStatus = LoadingStatus.Init;
    this.isDataChanged = false;
  };

  @computed get isValidationFailed() {
    return Object.values(this.errorMessages).some((msg) => msg !== null);
  }

  validateBeforeSave = () => {
    this.validateVat();
    this.validateVatRegNumber();
  };

  private get _formedApiData() {
    const { config, notes } = this.orderSettings;

    const formData = new FormData();

    if (config.certificate.size) {
      formData.append('certificate', config.certificate);
    }

    if (!config.certificateFilename) {
      formData.append('certificate_delete', '1');
    }

    const data = {
      vat: config.vat,
      vat_included: config.vatIncluded,
      taxable_delivery_fee: config.taxableDeliveryFee,
      vat_registration_number: config.vatRegistrationNumber,
      sms_edit_order: notes.smsEditOrder,
      allow_notes_at_checkout: notes.allowNotesAtCheckout,
      allow_item_notes: notes.allowItemNotes,
      upsales_frequency: notes.upsalesFrequency,
      allow_order_for_else: notes.allowOrderForElse,
      is_cc_zone_orders: notes.is_cc_zone_orders,
    };

    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    return formData;
  }
}

export const orderConfigurationStore = new OrderConfigurationStore();
